<template>
  <el-dialog :title="selectedItemId ? '編輯天氣預報' : '新增天氣預報'" :visible.sync="innerVisible" width="500px" :close-on-click-modal="false">
    <el-form :model="form" ref="form" :rules="rules" label-width="100px" @submit.native.prevent="submit" v-loading="loadingAsset">
      <el-form-item label="類型" prop="type">
        <el-select v-model="form.type" placeholder="請選擇天氣預報類型" style="width: 100%">
          <el-option label="7天預報" value="7d"></el-option>
          <el-option label="24小時預報" value="24h" disabled></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="城市名稱" prop="city">
        <el-input v-model="form.city" placeholder="例如：台北市">
          <el-button slot="append" icon="el-icon-search" @click="handleGeocode" :loading="geocoding">查詢座標</el-button>
        </el-input>
      </el-form-item>
      <el-form-item label="緯度 (lat)" prop="lat">
        <el-input v-model="form.lat" placeholder="例如：25.0330"></el-input>
      </el-form-item>
      <el-form-item label="經度 (lon)" prop="lon">
        <el-input v-model="form.lon" placeholder="例如：121.5654"></el-input>
      </el-form-item>
      <el-form-item label="檔案名稱" prop="name">
        <el-input v-model="form.name" placeholder="自動產生或手動修改"></el-input>
      </el-form-item>
      <el-form-item label="API URL" prop="src">
        <el-input v-model="form.src" type="textarea" :rows="3" placeholder="自動產生的天氣 API 網址"></el-input>
      </el-form-item>

      <div v-if="form.lat && form.lon" class="map-preview">
        <div class="map-label">座標預覽 (OpenStreetMap)：</div>
        <iframe 
          width="100%" 
          height="250" 
          frameborder="0" 
          scrolling="no" 
          marginheight="0" 
          marginwidth="0" 
          :src="mapUrl">
        </iframe>
      </div>
    </el-form>
    <div slot="footer" class="dialog-footer">
      <el-button @click="innerVisible = false">取 消</el-button>
      <el-button type="primary" @click="submit" :loading="submitting">確 定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import API from "@/api/";

export default {
  model: {
    prop: "visible",
    event: "toggleVisible",
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    parentId: {
      type: Number,
      default: null,
    },
    selectedItemId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      geocoding: false,
      submitting: false,
      loadingAsset: false,
      form: {
        parentId: null,
        type: "7d",
        city: "",
        lat: "",
        lon: "",
        name: "",
        src: "",
      },
      rules: {
        type: [{ required: true, message: "請選擇類型", trigger: "change" }],
        city: [{ required: true, message: "請輸入城市名稱", trigger: "blur" }],
        lat: [{ required: true, message: "請輸入緯度", trigger: "blur" }],
        lon: [{ required: true, message: "請輸入經度", trigger: "blur" }],
        name: [{ required: true, message: "請輸入資產名稱", trigger: "blur" }],
        src: [{ required: true, message: "API URL 不可為空", trigger: "blur" }],
      },
    };
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("toggleVisible", val);
      },
    },
    mapUrl() {
      if (!this.form.lat || !this.form.lon) return "";
      const lat = parseFloat(this.form.lat);
      const lon = parseFloat(this.form.lon);
      if (isNaN(lat) || isNaN(lon)) return "";
      
      const delta = 0.005; // 預覽範圍大小
      return `https://www.openstreetmap.org/export/embed.html?bbox=${lon - delta}%2C${lat - delta}%2C${lon + delta}%2C${lat + delta}&layer=mapnik&marker=${lat}%2C${lon}`;
    },
  },
  created() {
    this.form.parentId = this.parentId;
    if (this.selectedItemId) {
      this.loadAsset();
    }
  },
  watch: {
    "form.city": "updateAutoFields",
    "form.type": "updateAutoFields",
    "form.lat": "updateAutoFields",
    "form.lon": "updateAutoFields",
    visible(val) {
      if (val) {
        this.resetForm();
        this.form.parentId = this.parentId;
        if (this.selectedItemId) {
          this.loadAsset();
        }
      }
    },
    parentId(val) {
      this.form.parentId = val;
    },
  },
  methods: {
    resetForm() {
      this.form = {
        parentId: this.parentId,
        type: "7d",
        city: "",
        lat: "",
        lon: "",
        name: "",
        src: "",
      };
      if (this.$refs.form) {
        this.$refs.form.clearValidate();
      }
    },
    async loadAsset() {
      this.loadingAsset = true;
      try {
        const res = await API.asset.getAsset(this.selectedItemId);
        const asset = res.data;
        
        // Parse src to fill fields
        try {
          const url = new URL(asset.src);
          const pathParts = url.pathname.split('/');
          this.form.type = pathParts[pathParts.length - 1];
          
          const params = new URLSearchParams(url.search);
          this.form.lat = params.get("lat") || "";
          this.form.lon = params.get("lon") || "";
          this.form.city = params.get("city") || "";
        } catch (e) {
          console.error("Failed to parse weather src", e);
        }

        // Set these AFTER parsing to avoid updateAutoFields overwriting them too early
        this.$nextTick(() => {
          this.form.name = asset.name;
          this.form.src = asset.src;
        });
      } catch (err) {
        console.error(err);
        this.$message.error("載入資產失敗");
      } finally {
        this.loadingAsset = false;
      }
    },
    async handleGeocode() {
      if (!this.form.city) {
        this.$message.warning("請先輸入城市名稱");
        return;
      }
      this.geocoding = true;
      try {
        const res = await API.asset.geocodeCity(this.form.city);
        if (res.data && res.data.lat && res.data.lon) {
          this.form.lat = res.data.lat.toString();
          this.form.lon = res.data.lon.toString();
          this.$message.success("查詢成功");
        } else {
          this.$message.error("查無座標資訊，請手動輸入");
        }
      } catch (err) {
        console.error(err);
        this.$message.error("座標查詢失敗，請檢查 API 或手動輸入");
      } finally {
        this.geocoding = false;
      }
    },
    updateAutoFields() {
      // Skip auto update if we are loading
      if (this.loadingAsset) return;
      // Skip if fields are empty (likely initial state)
      if (!this.form.city && !this.form.lat) return;

      const typeLabel = this.form.type === "7d" ? "7天預報" : "24小時預報";
      const cityName = this.form.city || "未命名城市";
      this.form.name = `${cityName}-${typeLabel}.bmp`;
      
      const baseUrl = `https://api.ml2-mgmt.gebis.net/weather-bmp-api/${this.form.type}`;
      const params = new URLSearchParams();
      params.append("lat", this.form.lat || "0");
      params.append("lon", this.form.lon || "0");
      params.append("city", cityName);
      
      this.form.src = `${baseUrl}?${params.toString()}`;
    },
    submit() {
      this.$refs.form.validate(async (valid) => {
        if (valid) {
          if (!this.selectedItemId && !this.form.parentId) {
            this.$message.error("目錄 ID 錯誤，請重新開啟視窗");
            return;
          }
          this.submitting = true;
          try {
            if (this.selectedItemId) {
              await API.asset.renameAsset({
                id: this.selectedItemId,
                name: this.form.name,
                src: this.form.src,
              });
              this.$message.success("更新天氣預報成功");
            } else {
              await API.asset.createWeatherAsset({
                parentId: Number(this.form.parentId),
                name: this.form.name,
                src: this.form.src,
              });
              this.$message.success("新增天氣預報成功");
            }
            this.innerVisible = false;
            this.$emit("success");
          } catch (err) {
            console.error(err);
          } finally {
            this.submitting = false;
          }
        }
      });
    },
  },
};
</script>

<style scoped>
.map-preview {
  margin-top: 10px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
}
.map-label {
  padding: 5px 10px;
  background: #f5f7fa;
  font-size: 12px;
  color: #606266;
  border-bottom: 1px solid #e4e7ed;
}
</style>
